/**
 * ENG-7253: map the catalog remote-MCP descriptor across the manager seam.
 *
 * The `/host/agent-integrations` response forwards the integration's
 * `integration_definitions.remote_mcp` column in **snake_case** (`remote_mcp`,
 * added by ENG-6993 / ADR-0033 Slice 2). Every host-side consumer, however,
 * reads the **camelCase** `remoteMcp` on `ResolvedIntegration`
 * (`buildRemoteMcpEntry(definition_id, integration.remoteMcp)` in the
 * claudecode adapter's `writeIntegrations` / `buildMcpJson`).
 *
 * Nothing bridged the two names, so the forwarded descriptor was dropped at the
 * manager and `integration.remoteMcp` was always `undefined`. For a remote-MCP
 * integration with an `INTEGRATION_REGISTRY` fallback (anchor-browser) the
 * adapter still rendered from the code registry, hiding the gap; but a
 * **pure-catalog** remote-MCP integration with no registry entry (monday.com)
 * silently never rendered its server into `.mcp.json` and none of its
 * `mcp__<id>__*` tools could bind.
 *
 * This helper performs the one mapping that closes the seam. It is intentionally
 * additive (spreads the original object) so all other consumers - connectivity
 * probes, the integration hash, the OAuth refresh loop - see an unchanged shape.
 */
import type { RemoteMcpSpec } from '@augmented/core';

/** An integration object as it arrives from `/host/agent-integrations`. */
export interface HostIntegrationWithRemoteMcp {
  remote_mcp?: RemoteMcpSpec | null;
}

/**
 * Return the integration with `remoteMcp` populated from the forwarded
 * snake_case `remote_mcp` column (or `undefined` when absent). The original
 * fields are preserved verbatim.
 */
export function withResolvedRemoteMcp<T extends HostIntegrationWithRemoteMcp>(
  integration: T,
): T & { remoteMcp: RemoteMcpSpec | undefined } {
  return { ...integration, remoteMcp: integration.remote_mcp ?? undefined };
}
